/**
 * Future Self (blueprint SC-18): the version of themselves the member is
 * choosing to grow toward, written down once and used to frame today.
 *
 * The prompts are the blueprint's eight, plus the prototype's letter, which is
 * the only one of its five the blueprint does not already cover. The prototype
 * kept the whole thing in localStorage; here it is a row per horizon, because
 * a member should be able to hold a three-month picture and a five-year one at
 * the same time without either overwriting the other.
 */

export const FUTURE_SELF_HORIZONS = ['three_months', 'one_year', 'five_years'] as const;
export type FutureSelfHorizon = (typeof FUTURE_SELF_HORIZONS)[number];

export const FUTURE_SELF_HORIZON_LABELS: Record<FutureSelfHorizon, string> = {
  three_months: '3 months',
  one_year: '1 year',
  five_years: '5 years',
};

export const FUTURE_SELF_HORIZON_HINTS: Record<FutureSelfHorizon, string> = {
  three_months: 'Close enough to picture honestly. Far enough to be different.',
  one_year: 'Long enough for something real to change.',
  five_years: 'Far enough that you have to guess. Guess anyway.',
};

export const FUTURE_SELF_PROMPT_KEYS = [
  'money',
  'work',
  'body',
  'relationships',
  'time',
  'home',
  'places',
  'meaning',
  'letter',
] as const;
export type FutureSelfPromptKey = (typeof FUTURE_SELF_PROMPT_KEYS)[number];

export type FutureSelfPrompt = {
  key: FutureSelfPromptKey;
  label: string;
  prompt: string;
  /** A letter wants room; a one-line answer does not. */
  long?: boolean;
};

export const FUTURE_SELF_PROMPTS: readonly FutureSelfPrompt[] = [
  {
    key: 'money',
    label: 'Money',
    prompt:
      'What does financial success look like for this version of you? Describe it in numbers or in what it lets you do, whichever is truer.',
  },
  {
    key: 'work',
    label: 'Work',
    prompt: 'What work or business are you doing? Who is it for?',
  },
  {
    key: 'body',
    label: 'Your body',
    prompt:
      'How do you want to feel physically? Describe an ordinary morning rather than a best day.',
  },
  {
    key: 'relationships',
    label: 'People',
    prompt: 'Who is around you? What do those relationships feel like from the inside?',
  },
  {
    key: 'time',
    label: 'Your time',
    prompt: 'How do you spend your days? What is on the calendar and what is not?',
  },
  {
    key: 'home',
    label: 'Home',
    prompt: 'Where and how do you live? What do you see first when you walk in?',
  },
  {
    key: 'places',
    label: 'Where you go',
    prompt: 'Where do you go? What have you seen by then that you have not seen now?',
  },
  {
    key: 'meaning',
    label: 'Meaning',
    prompt: 'What gives your life meaning at that point? What would you be sorry to have skipped?',
  },
  {
    key: 'letter',
    label: 'A letter back',
    prompt:
      'Write a short letter from that version of you to the one reading this today. What do they want you to remember?',
    long: true,
  },
];

export const FUTURE_SELF_ANSWER_MAX = 2000;

export function isFutureSelfHorizon(value: string): value is FutureSelfHorizon {
  return (FUTURE_SELF_HORIZONS as readonly string[]).includes(value);
}

export function isFutureSelfPromptKey(value: string): value is FutureSelfPromptKey {
  return (FUTURE_SELF_PROMPT_KEYS as readonly string[]).includes(value);
}

/** How much of the picture is written, as a 0-100 index for the screen. */
export function futureSelfProgress(answers: Partial<Record<FutureSelfPromptKey, string>>): number {
  const written = FUTURE_SELF_PROMPTS.filter((p) => (answers[p.key] ?? '').trim().length > 0);
  return Math.round((written.length / FUTURE_SELF_PROMPTS.length) * 100);
}
